import { Theme, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { ModalResponsiveTableRequest } from './Modal';

export interface DataClicked {
  color: string;
  id: string;
  value: string;
  index: number;
  indexValue: string;
  data: any;
}

export interface DataSubcomponent {
  type: string;
  modal_trigger: string;
  additional_info_title: string;
  table_filter: string;
  lever_filter: string;
  lever_title: string[];
  data: {
    url: string;
  };
}

export interface DataContent {
  customTitle: string;
  customMarkers: boolean;
  customMarkerAxis: string;
  markerStrokeWidth: number;
  markerPosition: string;
  minWidthLine: number;
  margin: any;
  items: {
    subComponent: DataSubcomponent;
  };
  custom_color_dinamic: boolean;
  dynamic_keys: boolean;
  dynamic_line_keys: boolean;
  custom_percentage_tooltip: boolean;
  custom_key_order: boolean;
  keyBy: string;
  indexBy: string;
  lineDashed: boolean;
  colorBy: string;
  borderRadius: number;
  borderWidth: number;
  borderColor: any;
  padding: number;
  innerPadding: number;
  minValue: string;
  maxValue: string;
  groupMode: string;
  layout: string;
  reverse: boolean;
  valueScale: any;
  indexScale: any;
  axisTop: any;
  axisLeft: any;
  axisRight: any;
  axisBottom: any;
  isInteractive: boolean;
  labelOverBar: boolean;
  enableGridX: boolean;
  enableGridY: boolean;
  enableLabel: boolean;
  labelSkipWidth: number;
  labelSkipHeight: number;
  labelTextColor: string;
  theme: Theme;
  legends: any[];
  data: any[];
  colors: string[];
  keys: string[];
  lineKeys: any[];
  markers: any[];
}

interface RootObject {
  dataClicked: DataClicked;
  dataSubcomponent: DataSubcomponent;
  dataContent: DataContent;
}

const ModalResponsiveTableRequestContext = createContext(
  {} as { infoSubComponent: RootObject; [key: string]: any },
);

const ModalResponsiveTableRequestProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: { infoSubComponent?: RootObject; [key: string]: any };
}) => {
  const { cancelToken } = value;
  const [infoSubComponent, setInfoSubComponent] = useState<RootObject>(
    {} as RootObject,
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalSize = useBreakpointValue({ base: 'full', lg: '6xl' });

  const handleOpen = useCallback(
    (info) => {
      setInfoSubComponent(info);
      onOpen();
    },
    [onOpen],
  );

  const handleClose = useCallback(() => {
    setInfoSubComponent({} as RootObject);
    onClose();
  }, [onClose]);

  return (
    <ModalResponsiveTableRequestContext.Provider
      value={{
        isOpen,
        cancelToken,
        modalSize,
        infoSubComponent,
        handleOpen,
        handleClose,
      }}
    >
      <ModalResponsiveTableRequest />
      {children}
    </ModalResponsiveTableRequestContext.Provider>
  );
};

function useModalResponsiveTableRequestContext() {
  const context = useContext(ModalResponsiveTableRequestContext);

  if (!context) {
    throw new Error(
      'useModalResponsiveTableRequestContext deve ser usado dentro de um componente fornecido com o ModalResponsiveTableRequestContext.Provider',
    );
  }

  return context;
}

export {
  ModalResponsiveTableRequestProvider,
  useModalResponsiveTableRequestContext,
};
